// SB6-ANA-POP-04 — cross-window sync of the active filter so a filter widget
// popped onto a third screen actually re-filters the MAIN window.
//
// Each pop-out window is a same-origin SIBLING PAGE (a full, independent React
// app — not a portal), so the global filter Zustand store (`dataPointFilterStore`)
// and the AI widget's committed `aiClassFilter` (`aiFilterStore.committedFilter`)
// are PER-WINDOW, not shared. Without this hook, changing the AI/Defect filter in
// a popped window would only re-query that window — the main window's
// Filmstrip/Tiles/Viewer would not react.
//
// Decision D1 (option a): fold the committed `aiClassFilter` into the
// cross-window-synced filter state, exactly like the global selection's
// `defectTypeIds`. We carry BOTH on one `"filter"` BroadcastChannel message:
//   - `selection` — the 7-axis global data-point selection (so `defectTypeIds`,
//     `kind`, status, … cross windows), and
//   - `aiClassFilter` — the AI widget's committed wire JSON (or null).
// Every window's `useDataPointsQuery` / `useDataPointFacetsQuery` already read
// `dataPointFilterStore.selection` + `aiFilterStore.committedFilter`, so merging
// the incoming payload into those two stores makes the queries re-run with the
// popped filter's selection in EVERY window.
//
// What does NOT cross: the AI widget's internal table UI state (which rows are
// checked, slider positions — `aiFilterStore.config`). That stays local to the
// single window hosting the widget (pop-out MOVES the widget, so there is only
// one instance editing `config`). Only the *committed* result crosses.
//
// Persistence note: `aiClassFilter` is broadcast-ONLY — it is NOT written to the
// URL or the persisted `globalDataPointFilter` prefs here, so the non-popped
// case stays byte-identical. The receiving window writes the incoming selection
// straight into its store (NOT through `useDataPointFilter.setSelection`) so it
// does not re-persist / re-write the URL on a remote echo.
//
// Mirrors the structure of `useViewerPopoutSync` (image/selection/prefs): runs
// in BOTH windows, applies incoming messages guard-first so applying doesn't
// echo back, and a freshly-opened window posts `requestFilter` to hydrate.
import { useEffect, useRef } from "react";
import type { DataPointFilterSelection } from "@aeriosiq/types";
import { createSyncChannel, type SyncChannel } from "./popoutSync.js";
import { usePopoutWorkspaceStore } from "./popoutWorkspaceStore.js";
import {
  selectionsEqual,
  useDataPointFilterStore,
} from "../state/dataPointFilterStore.js";
import { useAiFilterStore } from "../state/aiFilterStore.js";

export interface UseFilterSyncArgs {
  readonly inspectionId: string;
  /** True in the popped-out child window (drives the late-open re-push). */
  readonly isPopout: boolean;
}

/** A `"filter"` payload is valid only if `selection` looks like the 7-axis
 *  shape (every axis present + an array). Guards against malformed messages. */
function isSelectionLike(value: unknown): value is DataPointFilterSelection {
  if (typeof value !== "object" || value === null) return false;
  const axes = [
    "status",
    "reviewState",
    "kind",
    "defectType",
    "component",
    "location",
    "severity",
  ] as const;
  const rec = value as Record<string, unknown>;
  return axes.every((a) => Array.isArray(rec[a]));
}

export function useFilterSync({
  inspectionId,
  isPopout,
}: UseFilterSyncArgs): void {
  const selection = useDataPointFilterStore((s) => s.selection);
  const setSelection = useDataPointFilterStore((s) => s.setSelection);
  const committedFilter = useAiFilterStore((s) => s.committedFilter);
  const setCommittedFilter = useAiFilterStore((s) => s.setCommittedFilter);
  const popoutCount = usePopoutWorkspaceStore((s) => s.windows.length);

  const enabled = isPopout || popoutCount > 0;

  const channelRef = useRef<SyncChannel | null>(null);
  // `undefined` = not yet initialised (skip the first post); thereafter the
  // last value we sent or received, so an applied value doesn't echo back.
  const lastSelRef = useRef<DataPointFilterSelection | undefined>(undefined);
  const lastAiRef = useRef<string | null | undefined>(undefined);
  // Latest local values, for the re-push when a child boots (`requestFilter`).
  const latestSelRef = useRef<DataPointFilterSelection>(selection);
  const latestAiRef = useRef<string | null>(committedFilter);
  latestSelRef.current = selection;
  latestAiRef.current = committedFilter;

  // Install the channel + apply incoming messages.
  useEffect(() => {
    if (!enabled || !inspectionId) return;
    const ch = createSyncChannel({ dataAcquisitionId: inspectionId });
    channelRef.current = ch;
    const off = ch.addListener((msg) => {
      if (msg.type === "filter") {
        if (isSelectionLike(msg.selection)) {
          lastSelRef.current = msg.selection;
          // Write straight into the store (NOT useDataPointFilter.setSelection)
          // so a remote echo doesn't re-persist / re-write the URL.
          setSelection(msg.selection);
        }
        lastAiRef.current = msg.aiClassFilter;
        setCommittedFilter(msg.aiClassFilter);
      } else if (msg.type === "requestFilter") {
        // A window just booted and asked for the current filter — answer with
        // our latest selection + committed AI filter so it hydrates at once.
        ch.postMessage({
          type: "filter",
          selection: latestSelRef.current,
          aiClassFilter: latestAiRef.current,
        });
      }
    });
    // As soon as we're listening, ask the other windows for the current filter
    // (BroadcastChannel does not buffer, so a late-opened window must pull).
    ch.postMessage({ type: "requestFilter" });
    return () => {
      off();
      ch.close();
      channelRef.current = null;
    };
  }, [enabled, inspectionId, setSelection, setCommittedFilter]);

  // Post local selection changes (incl. defectTypeIds, kind, status, …).
  useEffect(() => {
    if (!enabled) return;
    if (lastSelRef.current === undefined) {
      lastSelRef.current = selection; // initialise; don't post on mount
      return;
    }
    if (selectionsEqual(lastSelRef.current, selection)) return; // applied
    lastSelRef.current = selection;
    channelRef.current?.postMessage({
      type: "filter",
      selection,
      aiClassFilter: latestAiRef.current,
    });
  }, [enabled, selection]);

  // Post local committed-AI-filter changes.
  useEffect(() => {
    if (!enabled) return;
    if (lastAiRef.current === undefined) {
      lastAiRef.current = committedFilter; // initialise; don't post on mount
      return;
    }
    if (lastAiRef.current === committedFilter) return; // applied / unchanged
    lastAiRef.current = committedFilter;
    channelRef.current?.postMessage({
      type: "filter",
      selection: latestSelRef.current,
      aiClassFilter: committedFilter,
    });
  }, [enabled, committedFilter]);
}
